import { z } from "zod";
import {
  scriptRoleReviewChangeSchema,
  type ScriptRoleReviewChange,
} from "../../../shared/scriptRoleReview";

const REVIEW_DRAFT_STORAGE_VERSION = 1;
const REVIEW_DRAFT_STORAGE_PREFIX = "script-role-review-draft:";

export interface ReviewDraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ScriptRoleReviewDraftSnapshot {
  reviewId: string;
  reviewStartedAt: string;
  originalAssignmentSignature: string;
  draftSelections: Record<string, string>;
  changeHistory: ScriptRoleReviewChange[];
}

export type ScriptRoleReviewDraftLoadResult =
  | { status: "missing" }
  | { status: "invalid"; reason: string }
  | { status: "restored"; snapshot: ScriptRoleReviewDraftSnapshot };

const storedReviewDraftSchema = z.object({
  version: z.literal(REVIEW_DRAFT_STORAGE_VERSION),
  sessionKey: z.string().min(1),
  reviewId: z.uuid(),
  reviewStartedAt: z.iso.datetime(),
  originalAssignmentSignature: z.string().min(1),
  draftSelections: z.record(z.string().min(1), z.string().min(1)),
  changeHistory: z.array(scriptRoleReviewChangeSchema).max(2_000),
});

function storageKey(sessionKey: string) {
  return `${REVIEW_DRAFT_STORAGE_PREFIX}${sessionKey}`;
}

export function loadScriptRoleReviewDraft(input: {
  storage: ReviewDraftStorage;
  sessionKey: string;
  originalAssignmentSignature: string;
  tempRoleIds: string[];
}): ScriptRoleReviewDraftLoadResult {
  const raw = input.storage.getItem(storageKey(input.sessionKey));
  if (!raw) return { status: "missing" };

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(raw);
  } catch {
    return { status: "invalid", reason: "草稿 JSON 已损坏" };
  }

  const parsed = storedReviewDraftSchema.safeParse(parsedJson);
  if (!parsed.success) {
    return { status: "invalid", reason: "草稿版本或字段不兼容" };
  }
  if (
    parsed.data.sessionKey !== input.sessionKey ||
    parsed.data.originalAssignmentSignature !== input.originalAssignmentSignature
  ) {
    return { status: "invalid", reason: "草稿不属于当前匹配结果" };
  }

  const expectedRoleIds = new Set(input.tempRoleIds);
  const storedRoleIds = Object.keys(parsed.data.draftSelections);
  if (
    expectedRoleIds.size !== input.tempRoleIds.length ||
    storedRoleIds.length !== expectedRoleIds.size ||
    storedRoleIds.some((roleId) => !expectedRoleIds.has(roleId)) ||
    parsed.data.changeHistory.some((change) => !expectedRoleIds.has(change.tempRoleId))
  ) {
    return { status: "invalid", reason: "草稿角色集合与当前匹配不一致" };
  }

  return {
    status: "restored",
    snapshot: {
      reviewId: parsed.data.reviewId,
      reviewStartedAt: parsed.data.reviewStartedAt,
      originalAssignmentSignature: parsed.data.originalAssignmentSignature,
      draftSelections: { ...parsed.data.draftSelections },
      changeHistory: parsed.data.changeHistory.map((change) => ({ ...change })),
    },
  };
}

export function saveScriptRoleReviewDraft(input: {
  storage: ReviewDraftStorage;
  sessionKey: string;
  snapshot: ScriptRoleReviewDraftSnapshot;
}) {
  input.storage.setItem(
    storageKey(input.sessionKey),
    JSON.stringify({
      version: REVIEW_DRAFT_STORAGE_VERSION,
      sessionKey: input.sessionKey,
      ...input.snapshot,
    })
  );
}

export function removeScriptRoleReviewDraft(
  storage: ReviewDraftStorage,
  sessionKey: string
) {
  storage.removeItem(storageKey(sessionKey));
}
